// App.js
import { useState, useEffect, useMemo } from "react";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns }       from "@mui/x-date-pickers/AdapterDateFns";
import esLocale                from "date-fns/locale/es";

import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";

import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import routes from "routes";

import { filterRoutesByPerms, isPermsBypassOn } from "layouts/auth/permissions";
import { useAuth } from "layouts/auth/AuthContext";
import ImpersonationBar from "layouts/auth/ImpersonationBar";

import { useSoftUIController, setMiniSidenav, setOpenConfigurator } from "context";
import brand from "assets/images/logo_amprofon.png";

export default function App() {
  const { user, isImpersonating, isAuth } = useAuth();

  const [controller, dispatch] = useSoftUIController();
  const {
    direction       = "ltr",
    layout          = "dashboard",
    miniSidenav     = false,
    openConfigurator= false,
    sidenavColor    = "info",
  } = controller || {};

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  const isAuthRoute = pathname.startsWith("/authentication/");
  // 👇 solo mostramos el sidenav si hay sesión
  const showSidenav = isAuth && !isAuthRoute && layout === "dashboard";

  useMemo(() => {
    const cacheRtl = createCache({ key: "rtl", stylisPlugins: [rtlPlugin] });
    setRtlCache(cacheRtl);
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) return getRoutes(route.collapse);
      if (route.route)
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      return null;
    });

  const configsButton = (
    <SoftBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="default" color="inherit">
        settings
      </Icon>
    </SoftBox>
  );

  const perms = user?.permissions || {};

  // 👇 detección robusta de administrador (usuario efectivo)
  const isAdmin = !!(
    user?.is_admin ||               // boolean / 1 / true
    user?.isAdmin ||                // por si viene en camelCase
    user?.admin ||                  // por si el backend usa "admin"
    user?.role === "admin" ||       // rol de texto
    user?.permissions?.admin        // permiso admin dentro del objeto permisos
  );

  // ⛔ bypass de permisos:
  // - si el usuario (o el impersonado) es admin → ve todo
  // - o si tienes activado el bypass de dev
  const bypass = isAdmin || isPermsBypassOn();

  const sideRoutes = useMemo(
    () => (bypass ? routes : filterRoutesByPerms(routes, perms)),
    [bypass, perms]
  );

  // fallback según sesión
  const Fallback = (
    <Route
      path="*"
      element={<Navigate to={isAuth ? "/dashboard" : "/authentication/sign-in"} replace />}
    />
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeRTL}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
          {showSidenav && (
            <>
              <Sidenav
                key={`sidenav-${user?.id ?? "nouser"}-${isImpersonating ? "imp" : "norm"}`} // 👈 fuerza remount
                color={sidenavColor}
                brand={brand}
                brandName="AMPROFON"
                routes={sideRoutes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
              <Configurator />
              {configsButton}
              {isImpersonating && <ImpersonationBar />}
            </>
          )}

          {layout === "vr" && <Configurator />}

          <Routes>
            {getRoutes(sideRoutes)}
            {Fallback}
          </Routes>
        </LocalizationProvider>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {showSidenav && (
        <>
          <Sidenav
            key={`sidenav-${user?.id ?? "nouser"}-${isImpersonating ? "imp" : "norm"}`} // 👈 fuerza remount
            color={sidenavColor}
            brand={brand}
            brandName="AMPROFON"
            routes={sideRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
          {isImpersonating && <ImpersonationBar />}
        </>
      )}

      {layout === "vr" && <Configurator />}

      <Routes>
        {getRoutes(sideRoutes)}
        {Fallback}
      </Routes>
    </ThemeProvider>
  );
}